import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, withStyles } from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardDateTimePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, isValid, isSameDay, startOfDay } from "date-fns";
import { slotsForDay, gridSlotMinutes, dayStartOr } from "../../utils/calendarSlots";

// withStyles injects a `classes` prop; MUI components merge it into their
// own className, a raw "button" tag silently drops it. Everything here wraps
// Box with component="button" so the styles actually land.
const Segmented = withStyles(() => ({
  root: {
    display: "inline-flex",
    padding: 3,
    borderRadius: 999,
    backgroundColor: "#F1F2F4",
    marginBottom: 14,
  },
}))(Box);

const SegmentButton = withStyles(() => ({
  root: {
    border: "none",
    cursor: "pointer",
    borderRadius: 999,
    padding: "7px 18px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
    color: "#6B7280",
    backgroundColor: "transparent",
    transition: "background-color 140ms ease, color 140ms ease, box-shadow 140ms ease",
    "&[data-active='true']": {
      backgroundColor: "#ffffff",
      color: "#111827",
      boxShadow: "0 1px 2px rgba(17,24,39,0.10), 0 1px 3px rgba(17,24,39,0.06)",
    },
    "&:focus-visible": { outline: "2px solid #111827", outlineOffset: 2 },
  },
}))(Box);

const PartLabel = withStyles(() => ({
  root: {
    fontSize: 13,
    fontWeight: 600,
    color: "#666666",
    margin: "14px 0 7px",
    "&:first-child": { marginTop: 2 },
  },
}))(Typography);

const SlotGrid = withStyles(() => ({
  root: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(78px, 1fr))",
    gap: 7,
  },
}))(Box);

const Slot = withStyles(() => ({
  root: {
    position: "relative",
    border: "1px solid #E3E5E9",
    backgroundColor: "#ffffff",
    borderRadius: 9,
    padding: "9px 4px",
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
    color: "#374151",
    cursor: "pointer",
    textAlign: "center",
    transition:
      "transform 110ms cubic-bezier(.2,.8,.3,1), border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease",
    "&:hover:not(:disabled)": {
      borderColor: "#111827",
      boxShadow: "0 2px 6px rgba(17,24,39,0.08)",
      transform: "translateY(-1px)",
    },
    "&:active:not(:disabled)": { transform: "translateY(0)" },
    "&[data-selected='true']": {
      backgroundColor: "#111827",
      borderColor: "#111827",
      color: "#ffffff",
      boxShadow: "0 3px 10px rgba(17,24,39,0.22)",
    },
    // Off the studio's usual grid: an existing lesson at an odd time keeps
    // its chip, marked so the run of times does not just look broken.
    "&[data-offgrid='true']": { borderStyle: "dashed" },
    "&:disabled": {
      cursor: "not-allowed",
      color: "#C3C7CD",
      backgroundColor: "#F7F8F9",
      borderColor: "#EDEFF2",
      textDecoration: "line-through",
      textDecorationThickness: "1px",
    },
    "&:focus-visible": { outline: "2px solid #111827", outlineOffset: 2 },
  },
}))(Box);

// Deliberately not a scroll container. The modal is one already, and macOS
// hides overlay scrollbars until they move, so an inner scroller announced
// itself only by clipping a group heading in half.
const SlotList = withStyles(() => ({
  root: {
    marginTop: 2,
  },
}))(Box);

const DayRow = withStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 4,
  },
}))(Box);

// Morning / afternoon / evening is how a studio owner reads their own day,
// and it turns one long run of twenty chips into three scannable groups.
const partOf = (date) => {
  const h = date.getHours();
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
};

const overlaps = (start, end, ranges) =>
  ranges.some((range) => range.start < end && range.end > start);

/**
 * Picking a time used to mean dragging a clock dial. Studios think in slots,
 * so the slots lead and the dial is one tap away for the odd time that does
 * not sit on the grid.
 *
 * The chips are the calendar's own rows: same 7am phase, same step, both
 * derived from the studio's lesson length. busyRanges, when the caller has
 * them, strike out the times the instructor is already teaching.
 */
const TimeSlotPicker = ({
  value,
  onChange,
  lessonMinutes = 45,
  // The studio's first slot, in minutes from midnight (Edit Studio).
  dayStartMinutes,
  durationUnits = 1,
  busyRanges = [],
  disabled = false,
  // Called after a time chip is pressed (not on a day or clock change), so a
  // caller can fold the picker away once the time is chosen.
  onSlotPicked,
}) => {
  const hasValue = Boolean(value && isValid(value));
  const [mode, setMode] = useState("slots");
  const [day, setDay] = useState(hasValue ? startOfDay(value) : startOfDay(new Date()));

  // A time chosen on the clock tab, or handed in by a calendar click, moves
  // the strip to that day rather than stranding it on today.
  useEffect(() => {
    if (hasValue && !isSameDay(value, day)) setDay(startOfDay(value));
  }, [hasValue ? value.getTime() : null]);

  const step = gridSlotMinutes(lessonMinutes);
  const slotLength = (Number(lessonMinutes) || 45) * (Number(durationUnits) || 1) * 60000;

  const slots = useMemo(() => {
    const grid = slotsForDay(day, lessonMinutes, dayStartMinutes);
    // An existing lesson at a time the grid does not offer still needs a
    // chip, or it would read as unselected and the next click would move it.
    if (hasValue && isSameDay(value, day) && !grid.some((s) => s.getTime() === value.getTime())) {
      grid.push(new Date(value.getTime()));
      grid.sort((a, b) => a - b);
    }
    return grid;
  }, [day, lessonMinutes, dayStartMinutes, hasValue ? value.getTime() : null]);

  const normalisedBusy = useMemo(
    () =>
      (Array.isArray(busyRanges) ? busyRanges : [])
        .map((r) => ({ start: new Date(r.start), end: new Date(r.end) }))
        .filter((r) => isValid(r.start) && isValid(r.end)),
    [busyRanges]
  );

  const grouped = useMemo(() => {
    const groups = [];
    slots.forEach((slot) => {
      const part = partOf(slot);
      const last = groups[groups.length - 1];
      if (last && last.part === part) last.slots.push(slot);
      else groups.push({ part, slots: [slot] });
    });
    return groups;
  }, [slots]);

  const freeCount = slots.filter(
    (s) => !overlaps(s, new Date(s.getTime() + slotLength), normalisedBusy)
  ).length;

  const renderSlot = (slot) => {
    const end = new Date(slot.getTime() + slotLength);
    const busy = overlaps(slot, end, normalisedBusy);
    const selected = hasValue && value.getTime() === slot.getTime();
    const fromMidnight = slot.getHours() * 60 + slot.getMinutes();
    const offGrid = (fromMidnight - dayStartOr(dayStartMinutes)) % step !== 0;
    return (
      <Slot
        key={slot.toISOString()}
        component="button"
        type="button"
        role="option"
        aria-selected={selected}
        aria-label={`${format(slot, "h:mm a")}${busy ? ", already booked" : ""}`}
        data-selected={selected}
        data-offgrid={offGrid && !selected}
        disabled={disabled || (busy && !selected)}
        title={busy ? "Already teaching then" : undefined}
        onClick={() => {
          onChange(slot);
          if (onSlotPicked) onSlotPicked(slot);
        }}
      >
        {format(slot, "h:mm a")}
      </Slot>
    );
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box>
        <Segmented role="tablist" aria-label="How to choose a time">
          {[
            ["slots", "Time slots"],
            ["clock", "Clock"],
          ].map(([key, text]) => (
            <SegmentButton
              key={key}
              component="button"
              type="button"
              role="tab"
              aria-selected={mode === key}
              data-active={mode === key}
              onClick={() => setMode(key)}
            >
              {text}
            </SegmentButton>
          ))}
        </Segmented>

        {mode === "clock" ? (
          <KeyboardDateTimePicker
            value={hasValue ? value : null}
            onChange={(next) => onChange(next)}
            format="EE MM/dd hh:mm a"
            minutesStep={5}
            disabled={disabled}
            style={{ width: "100%" }}
            placeholder="Choose date and time"
          />
        ) : (
          <>
            <DayRow>
              <KeyboardDatePicker
                value={day}
                onChange={(next) => {
                  if (!next || !isValid(next)) return;
                  setDay(startOfDay(next));
                  // Moving a lesson to next Tuesday should not also forget
                  // that it was at 9:45.
                  if (hasValue) {
                    const kept = new Date(next);
                    kept.setHours(value.getHours(), value.getMinutes(), 0, 0);
                    onChange(kept);
                  }
                }}
                format="EEE MMM d"
                disabled={disabled}
                style={{ flex: 1 }}
                placeholder="Choose a day"
              />
              {normalisedBusy.length > 0 && (
                <Typography
                  style={{
                    fontSize: 12,
                    color: "#6B7280",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {freeCount} open
                </Typography>
              )}
            </DayRow>

            <SlotList role="listbox" aria-label="Available times">
              {grouped.map((group) => (
                <React.Fragment key={group.part}>
                  <PartLabel>{group.part}</PartLabel>
                  <SlotGrid>{group.slots.map(renderSlot)}</SlotGrid>
                </React.Fragment>
              ))}
            </SlotList>

            {normalisedBusy.length > 0 && (
              <Typography style={{ fontSize: 12, color: "#6B7280", marginTop: 10 }}>
                Struck-through times are already booked for this instructor.
              </Typography>
            )}
          </>
        )}
      </Box>
    </MuiPickersUtilsProvider>
  );
};

export default TimeSlotPicker;
